import { historyGet } from './HistoryRequests';
import { snakeToCamel } from '../utils/CaseUtils';
import { getCalendarHistorySelection } from '../utils/DateUtils/historyRange';

const endpoint = '/deliveryops/drivers/history/v2';
export const isHistoryChanged = (error) =>
  error?.status === 409 && /^history_changed\b/.test(error?.message || '');

const count = (value) => {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error('Invalid history count');
  return value;
};

const validate = (response, snapshot) => {
  if (typeof response?.snapshot !== 'string' || !response.snapshot ||
      response.snapshot_semantics !== 'revision_guard_restart_on_change' ||
      !Number.isSafeInteger(response.reference_time) ||
      response.currency !== 'USD' || response.monetary_unit !== 'dollars' ||
      response.monetary_encoding !== 'fixed_3_decimal_string') {
    throw new Error('Invalid calendar history metadata');
  }
  if (snapshot && snapshot !== response.snapshot) {
    throw Object.assign(new Error('history_changed: restart from page 1'), { status: 409 });
  }
};

const totals = (response) => {
  if (typeof response?.total_earned?.total !== 'string' ||
      !/^-?\d+\.\d{3}$/.test(response.total_earned.total) ||
      typeof response.earnings_complete !== 'boolean') {
    throw new Error('Invalid calendar history earnings');
  }
  const missing = count(response.missing_compensation_count);
  if ((response.missing_compensation_order_ids !== undefined &&
       (!Array.isArray(response.missing_compensation_order_ids) || missing !== response.missing_compensation_order_ids.length)) ||
      response.earnings_complete !== (missing === 0)) {
    throw new Error('Inconsistent missing compensation metadata');
  }
  return {
    earned: response.total_earned.total,
    deliveries: { done: count(response.completed_orders) },
    earningsStatus: missing ? 'incomplete' : 'ready',
    missingCompensationCount: missing,
    missingCompensationOrderIds: response.missing_compensation_order_ids,
  };
};

// Business windows are authoritative metadata, not client-side filter inputs.
const windows = (value) => {
  if (!Array.isArray(value)) throw new Error('Missing business timezone metadata');
  value.forEach((window) => {
    if (typeof window.timezone !== 'string' ||
        (window.timezone !== 'UTC' && !window.timezone.includes('/')) ||
        !Number.isFinite(window.utc_start) || !Number.isFinite(window.utc_end) ||
        window.utc_end <= window.utc_start) {
      throw new Error('Invalid business timezone metadata');
    }
    new Intl.DateTimeFormat('en', { timeZone: window.timezone });
  });
  return snakeToCamel(value);
};

export const getDriversCalendarHistory = async ({
  page = 1, dateRange, selection = getCalendarHistorySelection(dateRange), snapshot, signal,
}) => {
  if (page > 1 && !snapshot) throw new Error('Restart history from page 1');
  const params = { ...selection, page, ...(snapshot ? { snapshot } : {}) };
  const response = await historyGet(endpoint, params, signal);
  validate(response, snapshot);
  if (response.reference_time !== selection.reference_time) {
    throw new Error('Mismatched history reference time');
  }
  if (!Array.isArray(response.results)) throw new Error('Invalid driver history roster');
  count(response.total_results);
  count(response.total_pages);
  const businessWindows = windows(response.business_windows);
  const results = response.results.map((row) => {
    if (!Number.isSafeInteger(row.driver_id) || row.driver_id <= 0) {
      throw new Error('Invalid canonical driver ID');
    }
    return {
      ...snakeToCamel(row), id: row.driver_id,
      ...totals(row),
      businessWindows: windows(row.business_windows),
    };
  });
  if (new Set(results.map((driver) => driver.id)).size !== results.length) {
    throw new Error('Duplicate driver in history roster');
  }
  // The roster contract includes authoritative row and whole-selection totals.
  // Fetching each driver's identical aggregate blocks the page unnecessarily.
  const summary = totals(response);
  return {
    ...snakeToCamel(response), results, businessWindows,
    totalEarned: summary.earned,
    earningsStatus: summary.earningsStatus,
    missingCompensationCount: summary.missingCompensationCount,
  };
};

export const getDriverCalendarDetails = async ({ driverId, selection, snapshot, page = 1, signal }) => {
  if (!Number.isSafeInteger(driverId) || driverId <= 0 || !snapshot) throw new Error('Invalid driver history selection');
  const response = await historyGet(`${endpoint}/${driverId}`,
    { ...selection, snapshot, page, details: true, group: 'day' }, signal);
  validate(response, snapshot);
  if (response.reference_time !== selection.reference_time || !Array.isArray(response.results)) {
    throw new Error('Invalid driver history details');
  }
  windows(response.business_windows);
  count(response.total_pages);
  if (response.results.some((order) => !Number.isSafeInteger(order.order_id) || order.order_id <= 0)) {
    throw new Error('Invalid history order ID');
  }
  return { ...snakeToCamel(response), ...totals(response) };
};